import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { BookTextIcon, NotebookTextIcon, PlusIcon } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Empty, EmptyDescription, EmptyMedia } from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { courseListQueryOptions } from "@/hooks/query/course";
import { useSession } from "@/lib/auth-client";
import { createWebPageJsonLd, seo } from "@/lib/seo";
import * as m from "@/paraglide/messages";

import { AppLayout } from "../-components/app-layout";
import { PublicVersionsSection, UserVersionsSection } from "./-components/version-sections";
import { CourseCard } from "./courses/-components/course-card";
import { CreateCourseDialog } from "./courses/-components/create-course-dialog";

const searchSchema = z.object({
	tab: z.enum(["contents", "courses"]).optional().default("contents"),
});

export const Route = createFileRoute("/learn/")({
	component: RouteComponent,
	validateSearch: searchSchema,
	head: ({ match }) => {
		const title = m.learn_browsePageTitle();
		const description = m.learn_browseDescription();

		return seo({
			title,
			description,
			pathname: match.pathname,
			keywords: ["public study sessions", "interactive learning", "StudyOS learn"],
			structuredData: createWebPageJsonLd({
				title,
				description,
				pathname: match.pathname,
			}),
		});
	},
});

const COURSE_SKELETON_KEYS = ["c-1", "c-2", "c-3"] as const;

function RouteComponent() {
	const { data: session } = useSession();
	const isLoggedIn = !!session;
	const { tab } = Route.useSearch();
	const navigate = useNavigate();
	const [showCreateCourse, setShowCreateCourse] = useState(false);

	const handleTabChange = (value: string | number | null) => {
		if (value === "contents" || value === "courses") {
			navigate({ to: "/learn", search: { tab: value }, replace: true });
		}
	};

	return (
		<AppLayout>
			<div className="flex-1 overflow-y-auto p-6">
				<div className="mx-auto w-full max-w-3xl space-y-6">
					{isLoggedIn ? (
						<Tabs value={tab} onValueChange={handleTabChange}>
							<div className="flex items-center justify-between">
								<TabsList>
									<TabsTrigger value="contents">
										{m.course_contents()}
									</TabsTrigger>
									<TabsTrigger value="courses">{m.course_courses()}</TabsTrigger>
								</TabsList>
								<Button
									variant="outline"
									size="sm"
									nativeButton={false}
									render={<Link to="/learn/mistakes" />}
								>
									<NotebookTextIcon className="size-4" />
									{m.learn_mistakesTitle()}
								</Button>
							</div>

							<TabsContent value="contents">
								<div className="space-y-8 pt-2">
									<UserVersionsSection />
									<PublicVersionsSection />
								</div>
							</TabsContent>

							<TabsContent value="courses">
								<div className="space-y-4 pt-2">
									<div className="flex items-center justify-between">
										<h2 className="text-lg font-semibold">
											{m.course_courses()}
										</h2>
										<Button
											variant="outline"
											size="sm"
											onClick={() => setShowCreateCourse(true)}
										>
											<PlusIcon className="size-4" />
											{m.course_createNew()}
										</Button>
									</div>
									<CoursesSection
										onCreateCourse={() => setShowCreateCourse(true)}
									/>
								</div>
							</TabsContent>
						</Tabs>
					) : (
						<PublicVersionsSection />
					)}
				</div>
			</div>

			<CreateCourseDialog
				open={showCreateCourse}
				onOpenChange={setShowCreateCourse}
				onCreated={(courseId) => {
					navigate({ to: "/learn/courses/$courseId", params: { courseId } });
				}}
			/>
		</AppLayout>
	);
}

function CoursesSection({ onCreateCourse }: { onCreateCourse: () => void }) {
	const { data: courses, isPending } = useQuery(courseListQueryOptions());

	if (isPending) {
		return (
			<div className="space-y-3">
				{COURSE_SKELETON_KEYS.map((key) => (
					<Skeleton key={key} className="h-20 rounded-lg" />
				))}
			</div>
		);
	}

	if (!courses || courses.length === 0) {
		return (
			<Empty className="py-10">
				<EmptyMedia variant="icon">
					<BookTextIcon className="size-8" />
				</EmptyMedia>
				<EmptyDescription>{m.course_emptyCourses()}</EmptyDescription>
				<p className="text-xs text-muted-foreground">
					{m.course_emptyCoursesDescription()}
				</p>
				<Button variant="outline" size="sm" onClick={onCreateCourse}>
					<PlusIcon className="size-4" />
					{m.course_createNew()}
				</Button>
			</Empty>
		);
	}

	return (
		<div className="space-y-3">
			{courses.map((course) => (
				<CourseCard key={course.id} course={course} />
			))}
		</div>
	);
}
